import { Technology } from './model/general/Technology';

export class CalcUtil {

  static calculateTotalCapacity(unitCount: number, capacityPerUnit: number, factor: number): number {
    return parseFloat(((unitCount || 0) * (capacityPerUnit || 0) * factor).toFixed(2));
  }

  static calculateKpiStats(projects: KpiStatsInput[]): KpiStatsOutput {
    const result: KpiStatsOutput = {
      usedTechnologies: [],
      total: null,
      [Technology.WIND_ONSHORE]: null,
      [Technology.SOLAR]: null,
      [Technology.STORAGE]: null
    };
    const totalData = {
      mainCap: 0,
      unitCount: 0,
    };
    for (const technology of Object.values(Technology)) {
      const data = {
        projects: 0,
        mainCap: 0,
        altCap: 0,
        unitCount: 0,
      };
      for (const project of projects) {
        if (project.technologies.includes(technology)) {
          data.projects++;
        }
        data.mainCap += this.getMainCapacity(project, technology);
        data.altCap += this.getAltCapacity(project, technology);
        data.unitCount += this.getUnitCount(project, technology);
      }
      if (data.projects > 0) {
        totalData.mainCap += data.mainCap;
        totalData.unitCount += data.unitCount;

        const mainCap = this.roundCapacity(data.mainCap, 'W');
        const altCap = this.roundCapacity(data.altCap, this.getAltCapacityBaseUnit(technology));
        const units = this.roundModules(data.unitCount);

        result.usedTechnologies.push(technology);
        result[technology] = {
          projectCount: data.projects,
          mainCapacity: mainCap.roundedCapacity,
          mainCapacityUnit: mainCap.unit,
          altCapacity: altCap.roundedCapacity,
          altCapacityUnit: altCap.unit,
          unitCount: units.roundedCount,
          unitCountUnit: units.unit,
        };
      }
    }

    const totalMainCap = this.roundCapacity(totalData.mainCap, 'W');
    const totalUnits = this.roundModules(totalData.unitCount);

    result.total = {
      projectCount: projects.length,
      mainCapacity: totalMainCap.roundedCapacity,
      mainCapacityUnit: totalMainCap.unit,
      altCapacity: null,
      altCapacityUnit: null,
      unitCount: totalUnits.roundedCount,
      unitCountUnit: totalUnits.unit,
    };
    return result;
  }

  static getMainCapacity(project: KpiStatsInput, tech: Technology): number {
    switch (tech) {
      case Technology.WIND_ONSHORE: return project.capacityWindMw || 0;
      case Technology.SOLAR: return project.capacitySolarMw || 0;
      case Technology.STORAGE: return project.capacityStorageMw || 0;
    }
  }

  static getAltCapacity(project: KpiStatsInput, tech: Technology): number {
    switch (tech) {
      case Technology.WIND_ONSHORE: return 0;
      case Technology.SOLAR: return project.capacitySolarMwp || 0;
      case Technology.STORAGE: return project.capacityStorageMwh || 0;
    }
  }

  static getUnitCount(project: KpiStatsInput, tech: Technology): number {
    switch (tech) {
      case Technology.WIND_ONSHORE: return project.unitCountWind || 0;
      case Technology.SOLAR: return project.unitCountSolar || 0;
      case Technology.STORAGE: return project.unitCountStorage || 0;
    }
  }

  static getAltCapacityBaseUnit(tech: Technology): string {
    switch (tech) {
      case Technology.WIND_ONSHORE: return null;
      case Technology.SOLAR: return 'Wp';
      case Technology.STORAGE: return 'Wh';
    }
  }

  static roundCapacity(rawCapacity: number, baseUnit: string, fractionDigits: number = 2): {roundedCapacity: string, unit: string} {
    if (!baseUnit || rawCapacity === undefined || rawCapacity === null || rawCapacity < 0) {
      return {roundedCapacity: null, unit: null};
    }
    let divisor;
    let unitPrefix;
    if (rawCapacity >= 1000000) {
      divisor = 1000000;
      unitPrefix = 'T';
    } else if (rawCapacity >= 1000) {
      divisor = 1000;
      unitPrefix = 'G';
    } else {
      divisor = 1;
      unitPrefix = 'M';
    }
    return {roundedCapacity: (rawCapacity / divisor).toFixed(fractionDigits), unit: unitPrefix + baseUnit};
  }

  static roundModules(moduleCount: number, fractionDigits: number = 2): {roundedCount: string, unit: string} {
    if (moduleCount === undefined || moduleCount === null || moduleCount < 0) {
      return {roundedCount: null, unit: null};
    }
    let divisor;
    let unit;
    if (moduleCount >= 100_000_000) {
      divisor = 1_000_000_000;
      unit = 'B';
    } else if (moduleCount >= 100_000) {
      divisor = 1_000_000;
      unit = 'M';
    } else if (moduleCount >= 1000) {
      divisor = 1000;
      unit = 'K';
    } else {
      return {roundedCount: moduleCount + '', unit: ''};
    }
    return {roundedCount: (moduleCount / divisor).toFixed(fractionDigits), unit};
  }

}
export interface KpiStatsInput {
  technologies: Technology[];
  capacityWindMw: number;
  capacitySolarMw: number;
  capacitySolarMwp: number;
  capacityStorageMw: number;
  capacityStorageMwh: number;

  unitCountSolar: number;
  unitCountStorage: number;
  unitCountWind: number;
}
export interface KpiStatsOutput {
  usedTechnologies: Technology[];
  total: KpiStatsPerTechnology,
  [Technology.WIND_ONSHORE]: KpiStatsPerTechnology,
  [Technology.SOLAR]: KpiStatsPerTechnology,
  [Technology.STORAGE]: KpiStatsPerTechnology,
}
export interface KpiStatsPerTechnology {
  projectCount: number;
  mainCapacity: string;
  mainCapacityUnit: string;
  altCapacity: string;
  altCapacityUnit: string;
  unitCount: string;
  unitCountUnit: string;
}
